import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiArrowRight,
  FiDollarSign,
  FiEye,
  FiFileText,
  FiFilter,
  FiLoader,
  FiRefreshCw,
  FiRotateCcw,
  FiSearch,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";

export default function CreditNotesList() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [creditNotes, setCreditNotes] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const loadCreditNotes = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await apiRequest("/sales-credit-notes", {
        accessToken,
      });

      setCreditNotes(Array.isArray(response) ? response : []);
    } catch (requestError) {
      setError(requestError.message || "Could not load credit notes.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadCreditNotes();
  }, [loadCreditNotes]);

  const normalizedCreditNotes = useMemo(
    () =>
      creditNotes.map((creditNote) => {
        const salesReturn = creditNote.salesReturn || creditNote.return || null;

        const invoice =
          creditNote.salesInvoice ||
          creditNote.invoice ||
          salesReturn?.salesInvoice ||
          salesReturn?.invoice ||
          null;

        return {
          ...creditNote,
          amount: Number(
            creditNote.totalAmount ??
              creditNote.creditAmount ??
              creditNote.amount ??
              0,
          ),
          salesReturn,
          invoice,
          returnCode: salesReturn?.returnCode || "—",
          invoiceCode: invoice?.invoiceCode || "—",
          customerName:
            invoice?.customerNameSnapshot ||
            creditNote.customer?.fullName ||
            "Walk-in Customer",
          displayStatus: toTitle(creditNote.status),
        };
      }),
    [creditNotes],
  );

  const filteredCreditNotes = useMemo(() => {
    const query = search.trim().toLowerCase();

    return normalizedCreditNotes.filter((creditNote) => {
      const matchesSearch =
        !query ||
        creditNote.creditNoteCode?.toLowerCase().includes(query) ||
        creditNote.returnCode?.toLowerCase().includes(query) ||
        creditNote.invoiceCode?.toLowerCase().includes(query) ||
        creditNote.customerName?.toLowerCase().includes(query);

      const matchesStatus = status === "All" || creditNote.status === status;

      return matchesSearch && matchesStatus;
    });
  }, [normalizedCreditNotes, search, status]);

  const stats = useMemo(
    () => ({
      total: normalizedCreditNotes.length,
      issued: normalizedCreditNotes.filter((item) => item.status === "ISSUED")
        .length,
      voided: normalizedCreditNotes.filter((item) => item.status === "VOIDED")
        .length,
      issuedAmount: normalizedCreditNotes
        .filter((item) => item.status === "ISSUED")
        .reduce((total, item) => total + item.amount, 0),
    }),
    [normalizedCreditNotes],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <button
            onClick={() => navigate("/admindashboard/sales/returns")}
            className="flex items-center gap-2 text-gray-500 hover:text-primary-700 mb-4"
          >
            <FiArrowRight className="rotate-180" />
            Back to Sales Returns
          </button>

          <h1 className="text-4xl font-bold text-gray-900">Credit Notes</h1>

          <p className="mt-2 text-gray-500">
            Credit notes issued automatically after received sales returns.
          </p>
        </div>

        <button
          onClick={loadCreditNotes}
          disabled={isLoading}
          className="h-11 px-5 rounded-xl border border-gray-200 bg-white flex items-center gap-2 hover:bg-gray-50 disabled:opacity-60"
        >
          <FiRefreshCw className={isLoading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-red-700 flex gap-3">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
        <StatCard
          title="Total Credit Notes"
          value={stats.total}
          icon={<FiFileText size={24} />}
          color="bg-primary-50 text-primary-700"
        />

        <StatCard
          title="Issued"
          value={stats.issued}
          icon={<FiDollarSign size={24} />}
          color="bg-green-100 text-green-700"
        />

        <StatCard
          title="Voided"
          value={stats.voided}
          icon={<FiRotateCcw size={24} />}
          color="bg-red-100 text-red-700"
        />

        <StatCard
          title="Issued Credit Value"
          value={formatMoney(stats.issuedAmount)}
          icon={<FiDollarSign size={24} />}
          color="bg-blue-100 text-blue-700"
        />
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 p-6">
        <div className="grid lg:grid-cols-4 gap-4">
          <div className="relative lg:col-span-3">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search credit note, return, invoice, or customer..."
              className="w-full h-12 rounded-xl border border-gray-200 pl-11 pr-4 focus:outline-none focus:border-primary-700"
            />
          </div>

          <div className="flex gap-3">
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              className="w-full h-12 rounded-xl border border-gray-200 px-4 bg-white focus:outline-none focus:border-primary-700"
            >
              <option value="All">All Statuses</option>
              <option value="ISSUED">Issued</option>
              <option value="VOIDED">Voided</option>
            </select>

            <button
              onClick={() => {
                setSearch("");
                setStatus("All");
              }}
              title="Reset filters"
              className="h-12 w-12 shrink-0 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
            >
              <FiFilter />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100">
          <h2 className="text-2xl font-bold">Credit Note Register</h2>

          <p className="text-gray-500 mt-1">
            Showing {filteredCreditNotes.length} of{" "}
            {normalizedCreditNotes.length} credit notes
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead className="bg-gray-50">
              <tr>
                <TableHeader>Credit Note</TableHeader>
                <TableHeader>Sales Return</TableHeader>
                <TableHeader>Invoice</TableHeader>
                <TableHeader>Customer</TableHeader>
                <TableHeader>Date</TableHeader>
                <TableHeader right>Amount</TableHeader>
                <TableHeader center>Status</TableHeader>
                <TableHeader center>Actions</TableHeader>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="8" className="py-20 text-center text-gray-500">
                    <FiLoader className="inline animate-spin mr-2" />
                    Loading credit notes...
                  </td>
                </tr>
              ) : filteredCreditNotes.length ? (
                filteredCreditNotes.map((creditNote) => (
                  <tr
                    key={creditNote.id}
                    className="border-b border-gray-100 hover:bg-gray-50 transition"
                  >
                    <td className="px-5 py-5">
                      <h3 className="font-semibold text-primary-700">
                        {creditNote.creditNoteCode}
                      </h3>

                      <p className="text-xs text-gray-500 mt-1">
                        #{creditNote.creditNoteNumber || "—"}
                      </p>
                    </td>

                    <td className="px-5 py-5">{creditNote.returnCode}</td>

                    <td className="px-5 py-5 font-medium">
                      {creditNote.invoiceCode}
                    </td>

                    <td className="px-5 py-5">{creditNote.customerName}</td>

                    <td className="px-5 py-5">
                      {formatDate(
                        creditNote.issuedAt ||
                          creditNote.creditNoteDate ||
                          creditNote.createdAt,
                      )}
                    </td>

                    <td className="px-5 py-5 text-right font-semibold">
                      {formatMoney(creditNote.amount)}
                    </td>

                    <td className="px-5 py-5 text-center">
                      <CreditNoteStatusBadge
                        status={creditNote.displayStatus}
                      />
                    </td>

                    <td className="px-5 py-5">
                      <div className="flex justify-center">
                        <button
                          title="View credit note"
                          onClick={() =>
                            navigate(
                              `/admindashboard/sales/credit-notes/${creditNote.id}`,
                            )
                          }
                          className="h-9 w-9 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-primary-700 hover:text-white transition"
                        >
                          <FiEye />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="py-20 text-center">
                    <FiFileText
                      size={34}
                      className="mx-auto text-gray-300 mb-3"
                    />
                    <p className="font-medium text-gray-700">
                      No credit notes found
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      Credit notes appear here after sales returns are received.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon, color }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-6">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <h2 className="text-2xl font-bold mt-3">{value}</h2>
        </div>

        <div
          className={`h-14 w-14 rounded-2xl flex items-center justify-center ${color}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function TableHeader({ children, right = false, center = false }) {
  return (
    <th
      className={`px-5 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500 ${
        right ? "text-right" : center ? "text-center" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

function CreditNoteStatusBadge({ status }) {
  const colors = {
    Issued: "bg-green-100 text-green-700",
    Voided: "bg-red-100 text-red-700",
  };

  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${
        colors[status] || "bg-gray-100 text-gray-700"
      }`}
    >
      {status}
    </span>
  );
}

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(value) {
  if (!value) return "—";

  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function toTitle(value = "") {
  return value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
